/**
 * INT-0009 — template-aware appearance registry, shared by the admin
 * Appearance page and the public tenant site so pickers and rendering can
 * never disagree.
 *
 * - TEMPLATE_FONTS: the display-font choices each template offers (loaded
 *   faces only — every family here must be in the apps' Google Fonts link).
 * - PREMIER_HEROES: dark hero backgrounds for the premier ink hero — three
 *   generic (all sports) + three per sport where a curated set exists.
 * - CLASSIC_HERO_TREATMENTS: CSS-only identity-band treatments for classic
 *   (white page) — palette-reactive, no images required.
 * - paletteWorksOn(): COMPUTED palette↔template compatibility (grilling #5)
 *   — classic needs the primary readable on white, premier needs it
 *   readable against its own ink. No hand-tagging, so new palettes need
 *   zero curation.
 *
 * Absent settings mean "today's exact look" — none of these carry schema
 * defaults (CLAUDE.md config rule).
 */

import { SPORT_COLOR_PALETTES } from './banners';
import type { SportColorPalette } from './banners';

export type AppearanceTemplate = 'classic' | 'premier' | 'simple';

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

export interface TemplateFontOption {
  key: string;
  name: string;
  /** Full CSS stack — becomes --font-family-display for that template. */
  stack: string;
}

/** First entry of each list is the template's default (absent = this). */
export const TEMPLATE_FONTS: Record<'premier' | 'classic', TemplateFontOption[]> = {
  premier: [
    { key: 'oswald', name: 'Oswald', stack: "'Oswald', 'Arial Narrow', sans-serif" },
    { key: 'bebas', name: 'Bebas Neue', stack: "'Bebas Neue', 'Arial Narrow', sans-serif" },
    { key: 'anton', name: 'Anton', stack: "'Anton', Impact, sans-serif" },
  ],
  classic: [
    { key: 'inter', name: 'Inter', stack: "'Inter', 'Roboto', 'Open Sans', sans-serif" },
    { key: 'montserrat', name: 'Montserrat', stack: "'Montserrat', 'Trebuchet MS', sans-serif" },
    { key: 'playfair', name: 'Playfair Display', stack: "'Playfair Display', Georgia, serif" },
  ],
};

export function resolveTemplateFont(
  template: AppearanceTemplate,
  fontKey?: string | null,
): TemplateFontOption | null {
  if (template === 'simple') return null; // legacy look — no font choice
  const options = TEMPLATE_FONTS[template];
  return options.find((o) => o.key === fontKey) || options[0];
}

/**
 * Body (text) font choices — INT-0056 (owner 2026-10-30). ONE list for every
 * template, stored once per org as `settings.fonts.body`; headings keep the
 * template's display font above. The first entry is the default and its
 * stack is the pre-INT-0056 body stack, so an absent setting renders
 * byte-identical to before. Every family must be in the apps' Google Fonts
 * links (apps/app index.html, apps/next-app layout.tsx, apps/admin
 * index.html for the picker faces).
 */
export const BODY_FONTS: TemplateFontOption[] = [
  { key: 'inter', name: 'Inter', stack: "'Inter', 'Roboto', 'Open Sans', sans-serif" },
  { key: 'roboto', name: 'Roboto', stack: "'Roboto', 'Helvetica Neue', Arial, sans-serif" },
  { key: 'open-sans', name: 'Open Sans', stack: "'Open Sans', 'Segoe UI', Arial, sans-serif" },
  { key: 'barlow', name: 'Barlow', stack: "'Barlow', 'Helvetica Neue', Arial, sans-serif" },
  { key: 'lato', name: 'Lato', stack: "'Lato', 'Helvetica Neue', Arial, sans-serif" },
  { key: 'nunito', name: 'Nunito', stack: "'Nunito', 'Segoe UI', Arial, sans-serif" },
  { key: 'dm-sans', name: 'DM Sans', stack: "'DM Sans', 'Helvetica Neue', Arial, sans-serif" },
  { key: 'source-sans', name: 'Source Sans 3', stack: "'Source Sans 3', 'Source Sans Pro', 'Segoe UI', sans-serif" },
];

export const DEFAULT_BODY_FONT_KEY = BODY_FONTS[0].key;

/** Saved/preview key → option; unknown or absent → Inter (the default). */
export function resolveBodyFont(fontKey?: string | null): TemplateFontOption {
  return BODY_FONTS.find((o) => o.key === fontKey) || BODY_FONTS[0];
}

// ---------------------------------------------------------------------------
// Premier heroes (dark image backgrounds under the ink overlay)
// ---------------------------------------------------------------------------

export interface PremierHeroOption {
  key: string;
  name: string;
  /** 'generic' or a sport id from SPORT_COLOR_PALETTES. */
  sport: 'generic' | string;
  /** CSS background value rendered under the premier ink overlay — either a
   * url(...) for curated images or a gradient composition (generic set:
   * asset-free, deterministic, dark by construction). */
  background: string;
  /** INT-0014: set on image-backed heroes — the raw image URL and its
   * 768w mobile companion (generated by api script 42). */
  imageUrl?: string;
  mobileUrl?: string;
}

const CDN = 'https://staty-bucket.sfo2.cdn.digitaloceanspaces.com';

/**
 * Curated sets (grilling #2). The three GENERIC entries are CSS gradient
 * compositions — asset-free and dark by construction, so they suit every
 * sport and every palette. Sport sets use real CDN images (verified to
 * exist — never reference an unuploaded asset here); basketball ships
 * first, other sports fall back to the generic three until curated.
 */
export const PREMIER_HEROES: PremierHeroOption[] = [
  // Generic (all sports) — pure CSS, no assets
  {
    key: 'gen-arena', name: 'Arena glow', sport: 'generic',
    background: 'radial-gradient(70% 100% at 50% 0%, #3b3b4d 0%, #17171d 55%, #0b0b0f 100%)',
  },
  {
    key: 'gen-beams', name: 'Floodlight beams', sport: 'generic',
    background: 'linear-gradient(115deg, #101014 0%, #101014 38%, #23232e 42%, #101014 46%, #101014 60%, #1d1d26 64%, #101014 68%), linear-gradient(180deg, #16161c 0%, #0b0b0f 100%)',
  },
  {
    key: 'gen-weave', name: 'Dark weave', sport: 'generic',
    background: 'repeating-linear-gradient(45deg, #14141a 0, #14141a 12px, #191922 12px, #191922 24px)',
  },
  // Basketball — real CDN images (verified 2026-09-07). mobileUrl variants
  // (768w webp, INT-0014) are generated by api script 42 — keep the fields
  // in lockstep with a run of that script.
  { key: 'bb-crowd', name: 'Game night crowd', sport: 'basketball', background: `url(${CDN}/organizations/demo-elite/photos/photo-0.png)`, imageUrl: `${CDN}/organizations/demo-elite/photos/photo-0.png`, mobileUrl: `${CDN}/organizations/demo-elite/photos/photo-0-mobile.webp` },
  // Volleyball / pickleball / baseball / golf / tennis — generated by api
  // script 48 (2026-09-16, "all sports get 3"); mobile variants written in
  // the same run.
  { key: 'vb-arena', name: 'Center court', sport: 'volleyball', background: `url(${CDN}/appearance/premier-heroes/volleyball-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/volleyball-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/volleyball-0-mobile.webp` },
  { key: 'vb-ball', name: 'Ball on hardwood', sport: 'volleyball', background: `url(${CDN}/appearance/premier-heroes/volleyball-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/volleyball-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/volleyball-1-mobile.webp` },
  { key: 'vb-beach', name: 'Beach net at dusk', sport: 'volleyball', background: `url(${CDN}/appearance/premier-heroes/volleyball-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/volleyball-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/volleyball-2-mobile.webp` },
  { key: 'pb-court', name: 'Night court', sport: 'pickleball', background: `url(${CDN}/appearance/premier-heroes/pickleball-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/pickleball-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/pickleball-0-mobile.webp` },
  { key: 'pb-paddle', name: 'Paddle & ball', sport: 'pickleball', background: `url(${CDN}/appearance/premier-heroes/pickleball-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/pickleball-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/pickleball-1-mobile.webp` },
  { key: 'pb-dusk', name: 'Courts at dusk', sport: 'pickleball', background: `url(${CDN}/appearance/premier-heroes/pickleball-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/pickleball-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/pickleball-2-mobile.webp` },
  { key: 'ba-stadium', name: 'Night diamond', sport: 'baseball', background: `url(${CDN}/appearance/premier-heroes/baseball-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/baseball-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/baseball-0-mobile.webp` },
  { key: 'ba-leather', name: 'Ball & glove', sport: 'baseball', background: `url(${CDN}/appearance/premier-heroes/baseball-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/baseball-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/baseball-1-mobile.webp` },
  { key: 'ba-dusk', name: 'Game at dusk', sport: 'baseball', background: `url(${CDN}/appearance/premier-heroes/baseball-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/baseball-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/baseball-2-mobile.webp` },
  { key: 'gf-green', name: 'Green at dusk', sport: 'golf', background: `url(${CDN}/appearance/premier-heroes/golf-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/golf-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/golf-0-mobile.webp` },
  { key: 'gf-ball', name: 'Ball at the cup', sport: 'golf', background: `url(${CDN}/appearance/premier-heroes/golf-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/golf-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/golf-1-mobile.webp` },
  { key: 'gf-fairway', name: 'Misty fairway', sport: 'golf', background: `url(${CDN}/appearance/premier-heroes/golf-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/golf-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/golf-2-mobile.webp` },
  { key: 'tk-lanes', name: 'Night track', sport: 'track', background: `url(${CDN}/appearance/premier-heroes/track-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/track-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/track-0-mobile.webp` },
  { key: 'tk-blocks', name: 'Starting blocks', sport: 'track', background: `url(${CDN}/appearance/premier-heroes/track-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/track-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/track-1-mobile.webp` },
  { key: 'tk-dusk', name: 'Runners at dusk', sport: 'track', background: `url(${CDN}/appearance/premier-heroes/track-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/track-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/track-2-mobile.webp` },
  { key: 'jd-dojo', name: 'Quiet dojo', sport: 'judo', background: `url(${CDN}/appearance/premier-heroes/judo-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/judo-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/judo-0-mobile.webp` },
  { key: 'jd-gi', name: 'Gi & belt', sport: 'judo', background: `url(${CDN}/appearance/premier-heroes/judo-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/judo-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/judo-1-mobile.webp` },
  { key: 'jd-throw', name: 'Randori', sport: 'judo', background: `url(${CDN}/appearance/premier-heroes/judo-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/judo-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/judo-2-mobile.webp` },
  { key: 'tn-court', name: 'Night court', sport: 'tennis', background: `url(${CDN}/appearance/premier-heroes/tennis-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/tennis-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/tennis-0-mobile.webp` },
  { key: 'tn-ball', name: 'Ball & racquet', sport: 'tennis', background: `url(${CDN}/appearance/premier-heroes/tennis-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/tennis-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/tennis-1-mobile.webp` },
  { key: 'tn-dusk', name: 'Match at dusk', sport: 'tennis', background: `url(${CDN}/appearance/premier-heroes/tennis-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/tennis-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/tennis-2-mobile.webp` },
  // Soccer — generated by api script 48 (2026-09-16); mobile variants
  // written in the same run.
  { key: 'sc-floodlights', name: 'Night pitch', sport: 'soccer', background: `url(${CDN}/appearance/premier-heroes/soccer-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/soccer-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/soccer-0-mobile.webp` },
  { key: 'sc-ball', name: 'Ball on grass', sport: 'soccer', background: `url(${CDN}/appearance/premier-heroes/soccer-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/soccer-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/soccer-1-mobile.webp` },
  { key: 'sc-dusk', name: 'Match at dusk', sport: 'soccer', background: `url(${CDN}/appearance/premier-heroes/soccer-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/soccer-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/soccer-2-mobile.webp` },
  // Softball — generated by api script 48 (2026-09-16); mobile variants
  // written in the same run.
  { key: 'sb-lights', name: 'Friday night field', sport: 'softball', background: `url(${CDN}/appearance/premier-heroes/softball-0.png)`, imageUrl: `${CDN}/appearance/premier-heroes/softball-0.png`, mobileUrl: `${CDN}/appearance/premier-heroes/softball-0-mobile.webp` },
  { key: 'sb-leather', name: 'Ball & glove', sport: 'softball', background: `url(${CDN}/appearance/premier-heroes/softball-1.png)`, imageUrl: `${CDN}/appearance/premier-heroes/softball-1.png`, mobileUrl: `${CDN}/appearance/premier-heroes/softball-1-mobile.webp` },
  { key: 'sb-dusk', name: 'Game at dusk', sport: 'softball', background: `url(${CDN}/appearance/premier-heroes/softball-2.png)`, imageUrl: `${CDN}/appearance/premier-heroes/softball-2.png`, mobileUrl: `${CDN}/appearance/premier-heroes/softball-2-mobile.webp` },
  { key: 'bb-trophy', name: 'Trophy spotlight', sport: 'basketball', background: `url(${CDN}/organizations/demo-elite/photos/photo-1.png)`, imageUrl: `${CDN}/organizations/demo-elite/photos/photo-1.png`, mobileUrl: `${CDN}/organizations/demo-elite/photos/photo-1-mobile.webp` },
  { key: 'bb-art', name: 'Court art', sport: 'basketball', background: `url(${CDN}/organizations/demo-elite/banners/manual-2-raw.png)`, imageUrl: `${CDN}/organizations/demo-elite/banners/manual-2-raw.png`, mobileUrl: `${CDN}/organizations/demo-elite/banners/manual-2-raw-mobile.webp` },
];

export function premierHeroesForSport(sport?: string | null): PremierHeroOption[] {
  const sportSet = PREMIER_HEROES.filter((h) => h.sport === sport);
  const generic = PREMIER_HEROES.filter((h) => h.sport === 'generic');
  return [...sportSet, ...generic];
}

export function resolvePremierHero(heroKey?: string | null): PremierHeroOption | null {
  if (!heroKey) return null; // absent = today's gradient-only hero
  return PREMIER_HEROES.find((h) => h.key === heroKey) || null;
}

// ---------------------------------------------------------------------------
// Classic hero treatments (CSS-only, palette-reactive, on white)
// ---------------------------------------------------------------------------

export type ClassicHeroKey = 'clean' | 'tint' | 'lines' | 'glow' | 'dots' | 'grid';

export interface ClassicHeroOption {
  key: ClassicHeroKey;
  name: string;
  blurb: string;
}

export const CLASSIC_HERO_TREATMENTS: ClassicHeroOption[] = [
  { key: 'clean', name: 'Clean', blurb: 'Plain white, the current look.' },
  { key: 'tint', name: 'Tint', blurb: 'A soft wash of your colors behind the identity band.' },
  { key: 'lines', name: 'Lines', blurb: 'Subtle court-line pattern in your colors.' },
  { key: 'glow', name: 'Glow', blurb: 'Soft color glows from the corners.' },
  { key: 'dots', name: 'Dots', blurb: 'A fine halftone dot pattern in your color.' },
  { key: 'grid', name: 'Grid', blurb: 'A fine court-grid weave in your colors.' },
];

const CLASSIC_HERO_KEYS: ClassicHeroKey[] = CLASSIC_HERO_TREATMENTS.map((h) => h.key);

export function resolveClassicHero(heroKey?: string | null): ClassicHeroKey {
  return heroKey && (CLASSIC_HERO_KEYS as string[]).includes(heroKey)
    ? (heroKey as ClassicHeroKey)
    : 'clean';
}

/**
 * The ONE source of each treatment's CSS — rendered by the app hero AND the
 * admin picker preview (a second hand-coded copy WILL drift). All layers are
 * low-alpha washes over white: the primary carries the pattern, the RAW
 * secondary tints an edge (a white secondary degrades gracefully to the
 * primary-only look). Returns null for 'clean' (no styling at all).
 */
export function classicHeroCss(
  key: ClassicHeroKey,
  primary: string,
  secondary?: string | null,
): { background: string; backgroundSize?: string } | null {
  const s = secondary || primary;
  switch (key) {
    case 'tint':
      return { background: `linear-gradient(135deg, ${primary}14 0%, #ffffff 45%, ${s}14 100%)` };
    case 'lines':
      return { background: `repeating-linear-gradient(135deg, ${primary}0d 0, ${primary}0d 2px, transparent 2px, transparent 26px), linear-gradient(135deg, #ffffff 55%, ${s}0f 100%), #ffffff` };
    case 'glow':
      return { background: `radial-gradient(55% 75% at 10% 0%, ${primary}1f 0%, transparent 60%), radial-gradient(45% 65% at 100% 100%, ${s}1a 0%, transparent 55%), #ffffff` };
    case 'dots':
      return { background: `radial-gradient(${primary}21 1.5px, transparent 1.5px), #ffffff`, backgroundSize: '20px 20px' };
    case 'grid':
      return { background: `repeating-linear-gradient(0deg, ${primary}0a 0, ${primary}0a 1px, transparent 1px, transparent 28px), repeating-linear-gradient(90deg, ${primary}0a 0, ${primary}0a 1px, transparent 1px, transparent 28px), linear-gradient(135deg, #ffffff 60%, ${s}0d 100%), #ffffff` };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Palette ↔ template compatibility (computed, grilling #5)
// ---------------------------------------------------------------------------

function relativeLuminance(hex: string): number {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return 0;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Exported for the Essentials secondary-wash rule (apps' orgTheme,
// owner 2026-09-25) — same WCAG math the template gates use.
export function contrastRatio(hexA: string, hexB: string): number {
  const [hi, lo] = [relativeLuminance(hexA), relativeLuminance(hexB)].sort((a, b) => b - a);
  return (hi + 0.05) / (lo + 0.05);
}

/** The standard dark canvas premier falls back to when a palette's own
 * secondary is too light to serve as an ink. */
export const PREMIER_FALLBACK_INK = '#101014';

/**
 * Premier is dark BY DEFINITION — a palette whose secondary is light (e.g.
 * green_white's white) cannot supply the hero canvas, so premier substitutes
 * the standard dark ink and keeps the palette's primary untouched (owner
 * 2026-09-07: "keep that shade of green"). Palettes with a dark secondary
 * keep it as their ink.
 */
export function effectivePremierInk(secondary?: string | null): string {
  const ink = secondary || PREMIER_FALLBACK_INK;
  // AA body contrast for WHITE text, not just "darker than mid" (full-app
  // audit 2026-09-09): a mid-tone secondary (dark gold) passed the old
  // luminance gate and painted the whole premier canvas — hero, division
  // header band, tabs — in a color where white text reads at ~3:1.
  return contrastRatio('#ffffff', ink) >= 4.5 ? ink : PREMIER_FALLBACK_INK;
}

/** The neutral heading ink classic falls back to when a palette's secondary
 * is too light to read as TEXT on classic's white ground (Tailwind
 * gray-900 — classic's pre-existing heading color). */
export const CLASSIC_FALLBACK_INK = '#111827';

/**
 * Classic's mirror of effectivePremierInk (owner 2026-09-08: "the 2nd color
 * doesn't look like it's doing anything" on classic). Classic renders on
 * white, so the secondary serves as HEADING/TEXT ink — it must hold AA body
 * contrast (≥4.5:1) against white; light secondaries (red_white's white,
 * burgundy_cream's cream) fall back to the neutral gray-900 classic always
 * used, keeping those palettes' look byte-identical.
 */
export function effectiveClassicInk(secondary?: string | null): string {
  const ink = secondary || CLASSIC_FALLBACK_INK;
  return contrastRatio(ink, '#ffffff') >= 4.5 ? ink : CLASSIC_FALLBACK_INK;
}

/**
 * Classic's DECORATIVE accent — heading ticks, icon-badge tints (owner
 * 2026-09-08: blue_gold showed no gold anywhere on classic). Three tiers:
 * a secondary that already serves as the heading ink (≥4.5:1 on white)
 * leaves the accent to the primary (today's two-color look); a mid-tone
 * secondary that can't be text but IS visible on white (the gold family)
 * becomes the accent; a near-white secondary is invisible on classic's
 * ground, so the accent stays primary. Never use this for text.
 */
export function effectiveClassicAccent(primary: string, secondary?: string | null): string {
  if (!secondary) return primary;
  if (contrastRatio(secondary, '#ffffff') >= 4.5) return primary;
  if (contrastRatio(secondary, '#ffffff') >= 1.3) return secondary;
  return primary;
}

/**
 * Whether a palette suits a template's ground:
 * - classic renders on WHITE — the primary must hold ≥3:1 against white
 *   (WCAG large-text/graphics floor; kickers, ticks, links).
 * - premier renders on its EFFECTIVE ink (light secondaries are replaced by
 *   the dark fallback) — the primary must hold ≥3:1 against that ink.
 * - simple accepts everything (the legacy banner made its own rules).
 */
export function paletteWorksOn(
  template: AppearanceTemplate,
  palette: Pick<SportColorPalette, 'primary' | 'secondary'>,
): boolean {
  if (template === 'simple') return true;
  if (template === 'classic') return contrastRatio(palette.primary, '#ffffff') >= 3;
  return contrastRatio(palette.primary, effectivePremierInk(palette.secondary)) >= 3;
}

// ---------------------------------------------------------------------------
// Org palette resolution — INT-0057 custom colors
// ---------------------------------------------------------------------------

/** `settings.banner.colorPaletteKey` value meaning "use settings.banner.customColors". */
export const CUSTOM_PALETTE_KEY = 'custom';

export interface CustomColors { primary: string; secondary: string }

export interface ResolvedOrgPalette {
  /** Curated key, CUSTOM_PALETTE_KEY, or null for the sport-default fallback. */
  key: string | null;
  primary: string;
  secondary: string;
  /** True when the pair came from customColors (saved or previewed). */
  custom: boolean;
  /** True when the org (or a preview param) chose a palette; false = first-palette fallback. */
  explicit: boolean;
}

const HEX6 = /^#?([0-9a-f]{6})$/i;

/** `#RRGGBB` (uppercase) for a 6-digit hex with or without '#', else null. */
export function normalizeHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = HEX6.exec(value.trim());
  return m ? `#${m[1].toUpperCase()}` : null;
}

/** Both colors valid → normalized pair, else null. */
export function normalizeCustomColors(colors: unknown): CustomColors | null {
  const c = colors as { primary?: unknown; secondary?: unknown } | null | undefined;
  const primary = normalizeHex(c?.primary);
  const secondary = normalizeHex(c?.secondary);
  return primary && secondary ? { primary, secondary } : null;
}

/** Preview form `?colors=RRGGBB,RRGGBB` (hashes optional) → pair, else null. */
export function parseCustomColorsParam(value: string | null | undefined): CustomColors | null {
  if (!value) return null;
  const [p, s] = String(value).split(',');
  return normalizeCustomColors({ primary: p, secondary: s });
}

/** `RRGGBB,RRGGBB` for the preview param. */
export function customColorsParam(colors: CustomColors): string {
  return `${colors.primary.replace('#', '')},${colors.secondary.replace('#', '')}`;
}

/**
 * The ONE palette lookup for every consumer (public theme, banner
 * miniatures, emails, admin previews): saved key or `custom` + customColors,
 * with optional preview overrides (`?palette=` / `?colors=`), falling back to
 * the sport's first palette (explicit=false) so nothing ever renders without
 * colors. Curated tables are keyed per sport; a key unknown to the sport, or
 * `custom` without a valid pair, falls back the same way.
 */
export function resolveOrgPalette(
  sport: string | null | undefined,
  banner: { colorPaletteKey?: string | null; customColors?: unknown } | null | undefined,
  preview: { paletteKey?: string | null; colors?: CustomColors | null } = {},
): ResolvedOrgPalette {
  const table: Record<string, SportColorPalette> = (SPORT_COLOR_PALETTES as Record<string, Record<string, SportColorPalette>>)[sport || '']
    || SPORT_COLOR_PALETTES.basketball;
  const savedCustom = normalizeCustomColors(banner?.customColors);
  const pick = (key: string | null | undefined, colors: CustomColors | null): ResolvedOrgPalette | null => {
    if (!key) return null;
    if (key === CUSTOM_PALETTE_KEY) {
      return colors ? { key, ...colors, custom: true, explicit: true } : null;
    }
    const p = table[key];
    return p ? { key, primary: p.primary, secondary: p.secondary, custom: false, explicit: true } : null;
  };
  const resolved = pick(preview.paletteKey, preview.colors || savedCustom)
    || pick(banner?.colorPaletteKey, savedCustom);
  if (resolved) return resolved;
  const first = Object.values(table).sort((a, b) => a.order - b.order)[0];
  return { key: null, primary: first.primary, secondary: first.secondary, custom: false, explicit: false };
}
